'use client';


import { useMemo, useState } from 'react';
import {
  calculateMirrorGeometry,
  combMagnitudeResponse,
  reflectionCoefForSurface,
} from '@/lib/tools/combFilter';
import { toolFreqGrid } from '@/lib/tools/sbir';
import { buildNamedRoomStates, flatMagnitude } from '@/lib/tools/syntheticAcoustics';
import { resizeRoom, type RoomBounds } from '@/lib/tools/roomLayout';
import type { LengthUnit } from '@/lib/tools/units';
import ToolFreqChart from '@/components/tools/ToolFreqChart';
import { ToolMultiAuralization } from '@/components/tools/ToolAuralization';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import RoomPlan2D from '@/components/tools/RoomPlan2D';
import { CONTENT_NAME, SECOND_LINE } from '@/components/seo/roles';
import { selectedStateClass } from '@/components/room/chrome/selection';

const PLAN_BOUNDS: RoomBounds = {
  length: { min: 3, max: 10 },
  width: { min: 2.5, max: 8 },
};

export default function FirstReflectionAuralizerWidget() {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(5);
  const [widthM, setWidthM] = useState(4);
  const [heightM, setHeightM] = useState(2.6);
  const [speakerFrontM, setSpeakerFrontM] = useState(1);
  const [speakerSideM, setSpeakerSideM] = useState(0.8);
  const [listenerFrontM, setListenerFrontM] = useState(1.9);
  const [panelOn, setPanelOn] = useState(false);

  const applyRoom = (nextLength: number, nextWidth: number) => {
    const { room, contents } = resizeRoom(
      { lengthM, widthM },
      { lengthM: nextLength, widthM: nextWidth },
      {
        spk: { xM: speakerSideM, yM: speakerFrontM },
        lis: { xM: widthM / 2, yM: listenerFrontM },
      },
      { bounds: PLAN_BOUNDS },
    );
    setLengthM(room.lengthM);
    setWidthM(room.widthM);
    setSpeakerSideM(contents.spk.xM);
    setSpeakerFrontM(contents.spk.yM);
    setListenerFrontM(contents.lis.yM);
  };

  const { mirrors, primary } = useMemo(
    () =>
      calculateMirrorGeometry({
        lengthM,
        widthM,
        heightM,
        speakerDistFrontM: speakerFrontM,
        speakerSideDistM: speakerSideM,
        listenerDistFrontM: listenerFrontM,
      }),
    [lengthM, widthM, heightM, speakerFrontM, speakerSideM, listenerFrontM],
  );

  const freqs = useMemo(() => toolFreqGrid(100, 8000, 200), []);
  const delaySec = (primary?.delayMs ?? 5) / 1000;
  const magBare = useMemo(
    () =>
      combMagnitudeResponse(
        freqs,
        delaySec,
        reflectionCoefForSurface(primary?.surface ?? 'left', false),
      ),
    [freqs, delaySec, primary?.surface],
  );
  const magPanel = useMemo(
    () =>
      combMagnitudeResponse(
        freqs,
        delaySec,
        reflectionCoefForSurface(primary?.surface ?? 'left', true),
      ),
    [freqs, delaySec, primary?.surface],
  );
  const magPure = useMemo(() => flatMagnitude(freqs), [freqs]);
  const magActive = panelOn ? magPanel : magBare;

  const states = useMemo(
    () =>
      buildNamedRoomStates(freqs, [
        { id: 'pure', mag: magPure, nProducts: 0, rt60FineSec: 0.2, dryBroadband: true },
        { id: 'bare', mag: magBare, nProducts: 0, rt60FineSec: 0.5, dryBroadband: false },
        { id: 'panel', mag: magPanel, nProducts: 4, rt60FineSec: 0.28, dryBroadband: true },
      ]),
    [freqs, magPure, magBare, magPanel],
  );

  const sideMirrors = mirrors.filter((m) => m.surface === 'left' || m.surface === 'right');
  const rays = sideMirrors.flatMap((m) => {
    const mx = m.surface === 'left' ? 0 : widthM;
    return [
      {
        id: `r1-${m.surface}`,
        x1M: speakerSideM,
        y1M: speakerFrontM,
        x2M: mx,
        y2M: m.distFromFrontM,
      },
      {
        id: `r2-${m.surface}`,
        x1M: mx,
        y1M: m.distFromFrontM,
        x2M: widthM / 2,
        y2M: listenerFrontM,
      },
    ];
  });

  const result = primary
    ? `Primary reflection (${primary.surface}) arrives ${primary.delayMs.toFixed(1)} ms late — that comb filter is what you hear in the highs.`
    : 'Adjust positions to locate first-reflection mirror points.';

  const chip = (on: boolean, label: string, set: () => void) => (
    <button
      type="button"
      onClick={set}
      // SELECTION IS NOT AN ACTION: raised when chosen, never accent-filled
      // (AGENTS.md Button + selection rule) — this chip keeps its own
      // geometry (selectedStateClass carries only the state, per its doc).
      className={`flex-1 rounded-lg border px-3 py-2 text-body-sm font-semibold transition-colors ${selectedStateClass(on)}`}
      aria-pressed={on}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ToolDimSlider label="Length" valueM={lengthM} minM={3} maxM={10} unit={unit} onChangeM={(v) => applyRoom(v, widthM)} testId="dim-length" />
        <ToolDimSlider label="Width" valueM={widthM} minM={2.5} maxM={8} unit={unit} onChangeM={(v) => applyRoom(lengthM, v)} testId="dim-width" />
        <ToolDimSlider label="Height" valueM={heightM} minM={2} maxM={4} unit={unit} onChangeM={setHeightM} />
        <ToolDimSlider
          label="Speaker from front"
          valueM={speakerFrontM}
          minM={0.4}
          maxM={Math.max(0.5, lengthM - 0.5)}
          unit={unit}
          onChangeM={setSpeakerFrontM}
        />
        <ToolDimSlider
          label="Speaker from left"
          valueM={speakerSideM}
          minM={0.3}
          maxM={widthM / 2}
          unit={unit}
          onChangeM={setSpeakerSideM}
        />
        <ToolDimSlider
          label="Listener from front"
          valueM={listenerFrontM}
          minM={1}
          maxM={Math.max(1.1, lengthM - 0.5)}
          unit={unit}
          onChangeM={setListenerFrontM}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <RoomPlan2D
          lengthM={lengthM}
          widthM={widthM}
          unit={unit}
          resizeWalls
          roomBounds={PLAN_BOUNDS}
          onRoomSizeChange={applyRoom}
          markers={[
            { id: 'spk', xM: speakerSideM, yM: speakerFrontM, label: 'S', color: 'var(--rt-accent)' },
            { id: 'lis', xM: widthM / 2, yM: listenerFrontM, label: 'L', color: 'var(--rt-accent)' },
          ]}
          onMarkerMove={(id, x, y) => {
            if (id === 'spk') {
              setSpeakerSideM(x);
              setSpeakerFrontM(y);
            } else if (id === 'lis') {
              setListenerFrontM(y);
            }
          }}
          rays={rays}
          points={sideMirrors.map((m) => ({
            id: m.surface,
            xM: m.surface === 'left' ? 0 : widthM,
            yM: m.distFromFrontM,
            label: 'M',
            color: 'var(--rt-accent)',
          }))}
          testId="mirror-floorplan"
        />

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className={CONTENT_NAME}>Comb filter</h3>
          </div>
          <div className="flex gap-2" data-testid="panel-toggle-group">
            {chip(!panelOn, 'Bare wall', () => setPanelOn(false))}
            {chip(panelOn, 'Panel on', () => setPanelOn(true))}
          </div>
          <ToolFreqChart
            freqs={freqs}
            curves={[{ mag: magActive, tone: 'accent' }]}
            fMin={100}
            fMax={8000}
          />
          <ul className={`space-y-1.5 ${SECOND_LINE}`} data-testid="mirror-list">
            {mirrors.map((m) => (
              <li
                key={m.surface}
                className="flex justify-between border-b border-[var(--app-border)] py-1.5"
              >
                <span className="capitalize text-[var(--rt-ink)]">{m.surface}</span>
                <span className="font-mono">
                  {m.distFromFrontM.toFixed(2)} m · {m.delayMs.toFixed(1)} ms
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className={CONTENT_NAME}>Hear the reflection</h3>
        <ToolMultiAuralization
          states={states}
          stateButtons={[
            { id: 'pure', label: 'Pure' },
            { id: 'bare', label: 'Bare wall' },
            { id: 'panel', label: 'Panel on' },
          ]}
          defaultState="bare"
        />
      </div>

      <ToolLeadIn result={result} />
    </div>
  );
}
